<template>
    <AppModal :title="$t('updateProductContentModal.modalTitle')" ref="modal" @on-hide="hide">
        <div class="modal-body">
            <TaskProgressBar v-if="task.state.isRunning" :done="task.state.done" :total="task.state.total"/>
            <template v-else>
                <FormGroup :label="$t('updateProductContentModal.sourceProductLabel')">
                    <AppAutoComplete
                        :url="`/integrations/${this.$route.params.integrationUuid}/products`"
                        :model-value="sourceProduct?.uuid"
                        :selected-value="sourceProduct?.name"
                        @update:model-value="sourceProduct = $event"
                    >
                        <template #optionContent>
                            <div class="d-flex">
                                <img class="image-preview mr-1" :src="sourceProduct?.images?.[0] || PLACEHOLDER_IMAGE_URL" />
                                <span class="source-name">{{ sourceProduct?.name || $t('labels.empty') }}</span>
                            </div>
                        </template>
                        <template #optionItem="{option, index}">
                            <div class="d-flex">
                                <img class="image-preview mr-1" :src="option?.images?.[0] || PLACEHOLDER_IMAGE_URL" />
                                {{ option.name }}
                            </div>
                        </template>
                    </AppAutoComplete>
                </FormGroup>
                <div class="table-responsive">
                    <table class="table table-bordered table-fixed">
                        <thead>
                            <tr>
                                <th>{{ $t('labels.field') }}</th>
                                <th width="130px">
                                    <div class="d-flex align-items-center">
                                        <AppCheckbox :model-value="allValueIsChecked" @update:model-value="toggleSelectAll" class="mr-2" /> {{ $t('labels.update') }}
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="field in fieldsForUpdate" :key="field.key">
                                <td>{{ field.title }}</td>
                                <td>
                                    <AppCheckbox v-model="field.update" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
        </div>
        <div class="modal-footer d-flex justify-content-end" v-if="!task.state.isRunning">
            <AppButton color="indigo" :disabled="isUpdateDisabled" @click="startProcessing">
                {{ $t('buttons.update') }}
            </AppButton>
        </div>
    </AppModal>
</template>
<script>
import AppModal from '../../../../../../components/new/modal/AppModal.vue';
import FormGroup from '../../../../../../components/new/form/group/FormGroup.vue';
import AppAutoComplete from '../../../../../../components/new/form/autocomplete/AppAutoComplete.vue';
import AppButton from '../../../../../../components/new/button/AppButton.vue';
import AppCheckbox from '../../../../../../components/new/form/checkbox/AppCheckbox.vue';
import TaskProgressBar from '../../../../../../components/new/progress/TaskProgressBar.vue';

import { PLACEHOLDER_IMAGE_URL } from "../../../../../../constants";
import HttpRequest from "../../../../../../services/Http/HttpRequest";
import HttpSender from '../../../../../../services/Http/HttpSender';
import { createProgressTask } from '../../../../../../services/Progress/ProgressTask';

export default {
    name: 'UpdateProductContentModal',
    components: {
        AppModal, FormGroup, AppAutoComplete, AppButton, AppCheckbox, TaskProgressBar,
    },
    emits: ['update:table'],
    data() {
        return {
            PLACEHOLDER_IMAGE_URL,
            task: createProgressTask(),
            sourceProduct: null,
            productUuids: [],
            fieldsForUpdate: [
                {title: this.$t('labels.name'),         key: 'names',        update: false, },
                {title: this.$t('labels.description'),  key: 'descriptions', update: false, },
                {title: this.$t('labels.seo'),          key: 'seos',         update: false, },
                {title: this.$t('labels.price'),        key: 'price',        update: false, },
                {title: this.$t('labels.sale_price'),   key: 'sale_price',   update: false, },
                {title: this.$t('labels.balance'),      key: 'balance',      update: false, },
                {title: this.$t('labels.manufacturer'), key: 'manufacturer', update: false, },
                {title: this.$t('labels.measurement'),  key: 'measurement',  update: false, },
                {title: this.$t('labels.images'),       key: 'images',       update: false, },
            ]
        }
    },
    methods: {
        show(productUuids) {
            this.productUuids = productUuids;
            this.$refs.modal.show();
        },
        hide() {
            this.task.cancel();
            this.$refs.modal.hide();
            this.sourceProduct = null;
            this.fieldsForUpdate.forEach(field => {
                field.update = false;
            });
            this.productUuids = [];
        },
        toggleSelectAll(value) {
            this.fieldsForUpdate.forEach(field => {
                field.update = value;
            });
        },
        async startProcessing() {
            const updateRequests = this.collectUpdateRequests();

            const completed = await this.task.run(async ({setTotal, tick}) => {
                setTotal(updateRequests.length);

                await new HttpSender().sendRequestsInBatches(updateRequests, () => tick());
            });

            if (completed) {
                this.hide();
                this.$emit('update:table');
            }
        },
        collectUpdateRequests() {
            const updateRequests = [];
            const requestBody = {};

            for (const field of this.fieldsForUpdate) {
                if (!field.update) continue;

                let fieldValue = this.sourceProduct[field.key];

                if (field.key === 'descriptions' && Object.keys(fieldValue).length === 0) {
                    continue;
                }

                if (field.key === 'manufacturer') {
                    requestBody['manufacturer_name'] = fieldValue;
                    continue;
                }

                requestBody[field.key] = fieldValue;
            }

            for (const productUuid of this.productUuids) {
                updateRequests.push(
                    new HttpRequest('PATCH', `/integrations/products/${productUuid}`, requestBody)
                );
            }

            return updateRequests;
        },
    },
    computed: {
        allValueIsChecked() {
            return this.fieldsForUpdate.every(field => field.update);
        },
        isUpdateDisabled() {
            const noExample = !this.sourceProduct;
            const noFieldsSelected = !this.fieldsForUpdate.some(field => field.update);
            return noExample || noFieldsSelected;
        },
    },
    beforeUnmount() {
        this.task.cancel();
    }
}
</script>
<style scoped>
.image-preview {
    border-radius: 5px;
    object-fit: cover;
    object-position: center;
    width: 20px;
    height: 20px;
}
</style>